import { computed, readonly, ref } from 'vue'
import api from '@/services/api'

/**
 * ── how many items are waiting for a human, for the one row that should say so ─────────────────────
 *
 * 1,093 drafts sit in the review queue and the verdicts table has two rows. Five surfaces know that
 * number and not one of them is on the way in: the dashboard renders it as an unclickable figure in a
 * six-tile grid three screens down, and the sidebar row that actually opens the queue says «صف بازبینی»
 * and nothing else. A reviewer signing in has no way to learn there is work without opening the page
 * that has the work.
 *
 * This is the number for that row. It is modelled on `useSupportAttention`, which does the same job for
 * the support desk and already drives a sidebar badge, so the two counts behave identically: a
 * module-level singleton, a freshness window so that navigating does not re-ask, and one in-flight
 * promise shared by every caller.
 *
 * ── the count it exposes, and why it is `backlog_total` ────────────────────────────────────────────
 * The queue payload carries two counts on purpose. `total` is what THIS reviewer can still be given —
 * it shrinks when a colleague claims a page. `backlog_total` is every item still awaiting a human
 * regardless of who holds what. The controller separates them with a note about how conflating them is
 * «how a progress bar starts lying». A menu badge is a standing fact about the queue, not a fact about
 * one reviewer's next page, so it reads `backlog_total`.
 *
 * ── the cost, stated, because it is not free ───────────────────────────────────────────────────────
 * There is no count-only endpoint. `GET /question-bank/questions/review-queue` serializes FULL review
 * payloads — stem, options, concepts — so the counts ride along with content the sidebar does not want.
 * `limit=1` narrows that to a single item, which is one item too many but bounded and constant. If a
 * `.../review-queue/count` ever lands, this composable is the one place to repoint.
 *
 * ── a null is not a zero ───────────────────────────────────────────────────────────────────────────
 * `pending` starts null and STAYS null when the request fails. A badge rendering «۰» because the fetch
 * 403'd or timed out would tell a reviewer their queue is clear, which is the worst thing this panel
 * can do: make its own breakage look like health. The caller renders a badge only for a number.
 */
const backlog = ref(null)
const loading = ref(false)
const failed = ref(false)
const lastUpdatedAt = ref(null)
let inFlight = null

/** Every item still awaiting a human verdict, or null when we do not know. Never a fabricated zero. */
const pending = computed(() => backlog.value)

async function refresh({ force = false } = {}) {
  const isFresh = lastUpdatedAt.value && Date.now() - lastUpdatedAt.value.getTime() < 60_000
  if (!force && isFresh) return backlog.value
  if (inFlight) return inFlight

  loading.value = true
  inFlight = api
    .get('/question-bank/questions/review-queue', { params: { limit: 1 } })
    .then(({ data }) => {
      const payload = data?.data ?? {}
      const count = Number(payload.backlog_total ?? payload.total)

      backlog.value = Number.isFinite(count) ? count : null
      failed.value = backlog.value === null
      lastUpdatedAt.value = new Date()

      return backlog.value
    })
    .catch((error) => {
      // Left as null rather than zeroed. A reviewer must never be told the queue is empty by a
      // failed request; the row simply carries no badge, which is the honest absence of a number.
      failed.value = true
      console.error('review backlog', error)

      return backlog.value
    })
    .finally(() => {
      loading.value = false
      inFlight = null
    })

  return inFlight
}

/** Drop the cached count — used after a verdict so the badge cannot outlive the work it counts. */
function invalidate() {
  lastUpdatedAt.value = null
}

export function useReviewBacklog() {
  return {
    pending,
    loading: readonly(loading),
    failed: readonly(failed),
    lastUpdatedAt: readonly(lastUpdatedAt),
    refresh,
    invalidate,
  }
}
